/**
 * Pricing & Discounts Tab
 * Accessible to Hotel Owners and Managers
 * Contains: Group Booking Discounts, Package Deals
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Percent,
  Package,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  TrendingUp,
  DollarSign
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/backendUrl';


export function PricingDiscountsTab({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Group Discounts State
  const [groupDiscounts, setGroupDiscounts] = useState([]);
  
  // Package Deals State
  const [packageDeals, setPackageDeals] = useState([]);

  useEffect(() => {
    loadData();
  }, [hotelId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load group discounts
      const groupDiscountsRes = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/group-discounts`);
      const groupDiscountsData = await groupDiscountsRes.json();
      
      if (groupDiscountsData.success) {
        setGroupDiscounts(groupDiscountsData.discounts || []);
      }

      // Load package deals
      const packagesRes = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/package-deals`);
      const packagesData = await packagesRes.json();
      
      if (packagesData.success) {
        setPackageDeals(packagesData.packages || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  // Group Discounts Management
  const addGroupDiscount = () => {
    setGroupDiscounts([...groupDiscounts, {
      id: `discount_${Date.now()}`,
      name: '',
      min_rooms: 1,
      min_nights: 1,
      discount_percent: 0,
      active: true
    }]);
  };

  const updateGroupDiscount = (id, field, value) => {
    setGroupDiscounts(groupDiscounts.map(discount => 
      discount.id === id ? { ...discount, [field]: value } : discount
    ));
  };

  const removeGroupDiscount = (id) => {
    setGroupDiscounts(groupDiscounts.filter(discount => discount.id !== id));
  };

  const handleSaveGroupDiscounts = async () => {
    const invalid = groupDiscounts.filter(d => !d.name || d.min_rooms <= 0 || d.min_nights <= 0 || d.discount_percent <= 0);
    if (invalid.length > 0) {
      toast.error('Please fill in all fields correctly');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/group-discounts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discounts: groupDiscounts })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Group discounts saved successfully! Bitsy AI will now offer these to guests.');
      } else {
        toast.error(data.error || 'Failed to save group discounts');
      }
    } catch (error) {
      console.error('Failed to save group discounts:', error);
      toast.error('Failed to save group discounts');
    } finally {
      setSaving(false);
    }
  };

  // Package Deals Management
  const addPackageDeal = () => {
    setPackageDeals([...packageDeals, {
      id: `package_${Date.now()}`,
      name: '',
      meeting_room_id: '',
      guest_rooms_count: 1,
      duration_type: 'full_day',
      package_price: 0,
      active: true
    }]);
  };

  const updatePackageDeal = (id, field, value) => {
    setPackageDeals(packageDeals.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const removePackageDeal = (id) => {
    setPackageDeals(packageDeals.filter(pkg => pkg.id !== id));
  };

  const handleSavePackageDeals = async () => {
    const invalid = packageDeals.filter(p => !p.name || !p.meeting_room_id || p.guest_rooms_count <= 0 || p.package_price <= 0);
    if (invalid.length > 0) {
      toast.error('Please fill in all package fields correctly');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/package-deals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packages: packageDeals })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Package deals saved successfully');
      } else {
        toast.error(data.error || 'Failed to save package deals');
      }
    } catch (error) {
      console.error('Failed to save package deals:', error);
      toast.error('Failed to save package deals');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading pricing settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricing-discounts-content">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold heading-font mb-2 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            Pricing & Discounts
          </h2>
          <p className="text-muted-foreground">Manage group rates, package deals, and promotional pricing</p>
        </div>
      </div>

      {/* Group Booking Discounts Section */}
      <Card data-testid="group-discounts-card" className="border-indigo-200">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Percent className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="heading-font text-indigo-900">Group Booking Discounts</CardTitle>
              <CardDescription className="mt-1">
                Configure tiered discounts based on number of rooms + nights. "Stay more, save more!"
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <Alert className="bg-indigo-50 border-indigo-200">
            <AlertCircle className="w-4 h-4 text-indigo-600" />
            <AlertDescription className="text-sm text-indigo-900">
              <strong>🤖 Bitsy AI Integration:</strong> These discounts will automatically be offered to guests booking online through your AI assistant!
            </AlertDescription>
          </Alert>

          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Example: 5 rooms + 3 nights = 8% off | 10 rooms + 1 night = 10% off
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={addGroupDiscount}
              data-testid="add-group-discount-button"
              className="border-indigo-300 hover:bg-indigo-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Discount Rule
            </Button>
          </div>

          {groupDiscounts.length === 0 ? (
            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                No group discounts configured. Add rules to encourage group bookings and longer stays!
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {groupDiscounts.map((discount, index) => (
                <div
                  key={discount.id}
                  className="p-5 rounded-lg border bg-card space-y-4"
                  data-testid={`group-discount-${index}`}
                >
                  <div className="space-y-2">
                    <Label htmlFor={`discount-name-${discount.id}`}>
                      Rule Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id={`discount-name-${discount.id}`}
                      value={discount.name}
                      onChange={(e) => updateGroupDiscount(discount.id, 'name', e.target.value)}
                      placeholder="e.g., Small Group - Extended Stay"
                      data-testid={`discount-name-${index}`}
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`min-rooms-${discount.id}`}>
                        Min Rooms <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id={`min-rooms-${discount.id}`}
                        type="number"
                        min="1"
                        value={discount.min_rooms}
                        onChange={(e) => updateGroupDiscount(discount.id, 'min_rooms', parseInt(e.target.value))}
                        data-testid={`min-rooms-${index}`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`min-nights-${discount.id}`}>
                        Min Nights <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id={`min-nights-${discount.id}`}
                        type="number"
                        min="1"
                        value={discount.min_nights}
                        onChange={(e) => updateGroupDiscount(discount.id, 'min_nights', parseInt(e.target.value))}
                        data-testid={`min-nights-${index}`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`discount-percent-${discount.id}`}>
                        Discount (%) <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id={`discount-percent-${discount.id}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={discount.discount_percent}
                        onChange={(e) => updateGroupDiscount(discount.id, 'discount_percent', parseFloat(e.target.value))}
                        data-testid={`discount-percent-${index}`}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={discount.active}
                        onChange={(e) => updateGroupDiscount(discount.id, 'active', e.target.checked)}
                        className="rounded"
                      />
                      <Label className="text-sm">Active</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeGroupDiscount(discount.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`remove-discount-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSaveGroupDiscounts}
              disabled={saving || groupDiscounts.length === 0}
              size="lg"
              data-testid="save-group-discounts-button"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Group Discounts'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Package Deals Section */}
      <Card data-testid="package-deals-card" className="border-indigo-200">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="heading-font text-indigo-900">Package Deals</CardTitle>
              <CardDescription className="mt-1">
                Bundle meeting rooms with guest rooms for special event packages
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Example: Corporate Event Package = 1 Meeting Room + 3 Guest Rooms
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={addPackageDeal}
              data-testid="add-package-deal-button"
              className="border-indigo-300 hover:bg-indigo-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Package
            </Button>
          </div>

          {packageDeals.length === 0 ? (
            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                No package deals configured. Create bundles to increase event bookings!
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {packageDeals.map((pkg, index) => (
                <div
                  key={pkg.id}
                  className="p-5 rounded-lg border bg-card space-y-4"
                  data-testid={`package-deal-${index}`}
                >
                  <div className="space-y-2">
                    <Label htmlFor={`package-name-${pkg.id}`}>
                      Package Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id={`package-name-${pkg.id}`}
                      value={pkg.name}
                      onChange={(e) => updatePackageDeal(pkg.id, 'name', e.target.value)}
                      placeholder="e.g., Corporate Event Package"
                      data-testid={`package-name-${index}`}
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`meeting-room-${pkg.id}`}>
                        Meeting Room <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id={`meeting-room-${pkg.id}`}
                        value={pkg.meeting_room_id}
                        onChange={(e) => updatePackageDeal(pkg.id, 'meeting_room_id', e.target.value)}
                        placeholder="Meeting Room ID"
                        data-testid={`meeting-room-${index}`}
                      />
                      <p className="text-xs text-muted-foreground">Room ID or name</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`guest-rooms-count-${pkg.id}`}>
                        Guest Rooms <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id={`guest-rooms-count-${pkg.id}`}
                        type="number"
                        min="1"
                        value={pkg.guest_rooms_count}
                        onChange={(e) => updatePackageDeal(pkg.id, 'guest_rooms_count', parseInt(e.target.value))}
                        data-testid={`guest-rooms-${index}`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`package-price-${pkg.id}`}>
                        Package Price ($) <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id={`package-price-${pkg.id}`}
                        type="number"
                        min="0"
                        step="1"
                        value={pkg.package_price}
                        onChange={(e) => updatePackageDeal(pkg.id, 'package_price', parseFloat(e.target.value))}
                        data-testid={`package-price-${index}`}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`duration-type-${pkg.id}`}>Duration Type</Label>
                    <Select
                      value={pkg.duration_type}
                      onValueChange={(value) => updatePackageDeal(pkg.id, 'duration_type', value)}
                    >
                      <SelectTrigger id={`duration-type-${pkg.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="half_day">Half Day</SelectItem>
                        <SelectItem value="full_day">Full Day</SelectItem>
                        <SelectItem value="multi_day">Multi-Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={pkg.active}
                        onChange={(e) => updatePackageDeal(pkg.id, 'active', e.target.checked)}
                        className="rounded"
                      />
                      <Label className="text-sm">Active</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removePackageDeal(pkg.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`remove-package-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSavePackageDeals}
              disabled={saving || packageDeals.length === 0}
              size="lg"
              data-testid="save-package-deals-button"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Package Deals'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
